import React, { useRef, useState } from "react"
import Card from "@material-ui/core/Card"
import IconButton from "@material-ui/core/IconButton"
import Tooltip from "@material-ui/core/Tooltip"
import CircularProgress from "@material-ui/core/CircularProgress"
import moment from 'moment'
import axios from 'axios'

import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'

import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import GetAppIcon from '@material-ui/icons/GetApp'
import { t } from "i18next"

function CoafBatchCard(props) {
    const { flexColumnStyles, batch, refreshList, setBatchDialogState, setSnackbarContent } = props

    const [isUploading, setIsUploading] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
    const [openGenerateConfirm, setOpenGenerateConfirm] = useState(false)

    const fileInputRef = useRef(null)
    const isDraft = batch.status === 'draft'
    const isPendingSubmission = batch.status === 'pending_submission'

    const openViewDialog = () => {
        setBatchDialogState({
            open: true,
            mode: "view",
            batch: batch
        })
    }

    const openEditDialog = () => {
        if (!isDraft) return
        setBatchDialogState({
            open: true,
            mode: "edit",
            batch: batch
        })
    }

    const handleDeleteClick = () => {
        if (!isDraft) return
        setOpenDeleteConfirm(true)
    }

    const handleCloseDeleteConfirm = () => {
        setOpenDeleteConfirm(false)
        if (refreshList) refreshList()
    }

    const handleCloseGenerateConfirm = () => {
        setOpenGenerateConfirm(false)
    }

    const confirmDelete = async () => {
        setIsDeleting(true)
        try {
            await axios.delete(`/dash/alerts/occurrence_batch/${batch.occurrence_batch_key}`)
            setOpenDeleteConfirm(false)
            if (refreshList) refreshList()
        } catch (error) {
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao excluir o lote. Por favor, tente novamente."
            })
        } finally {
            setIsDeleting(false)
        }
    }

    const executeDownload = async () => {
        try {
            const response = await axios.get(`/dash/alerts/occurrence_batch/${batch.occurrence_batch_key}/xml`)
            const downloadUrl = response.data.presigned_url
    
            if (downloadUrl) {
                const link = document.createElement('a')
                link.href = downloadUrl
                link.setAttribute('download', `${batch.occurrence_batch_external_id || 'batch'}.xml`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            } else {
                throw new Error("URL de download não encontrada.")
            }

        } catch (error) {
            console.error("Error downloading XML:", error)
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao baixar o arquivo XML. Por favor, tente novamente."
            })
        } finally {
            setIsDownloading(false)
        }
    }

    const handleDownloadClick = async () => {
        if (isDraft) {
            setOpenGenerateConfirm(true)
        } else {
            setIsDownloading(true)
            try {
                await executeDownload()
            } catch (error) {
                console.error("Error downloading XML:", error)
                alert("Erro ao baixar o arquivo XML.")
            } finally {
                setIsDownloading(false)
            }
        }
    }

    const confirmGenerateAndDownload = async () => {
        setIsDownloading(true)
        try {
            await axios.post(`/dash/alerts/occurrence_batch/${batch.occurrence_batch_key}/xml`, {})
            await executeDownload()

            setOpenGenerateConfirm(false)
            if (refreshList) refreshList()

        } catch (error) {
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao processar o arquivo XML. Por favor, tente novamente."
            })
        } finally {
            setIsDownloading(false)
        }
    }

    const handleUploadClick = () => {
        fileInputRef.current.click()
    }

    const handleFileChange = async (event) => {
        const file = event.target.files[0]
        if (!file) return

        setIsUploading(true)
        const reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onload = async () => {
            try {
                const base64Content = reader.result.split(',')[1]
                await axios.post(`/dash/alerts/occurrence_batch/${batch.occurrence_batch_key}/receipt`, {
                    receipt_file_b64: base64Content
                })
                setSnackbarContent({
                    type: "success",
                    message: "Recibo enviado com sucesso!"
                })
                handleCloseDeleteConfirm()
            } catch (error) {
                if (error.response.status === 400) {
                    setSnackbarContent({
                        type: "error",
                        message: "O arquivo de recibo enviado não é válido. Verifique se o arquivo se o arquivo está correto e tente novamente."
                    })
                } else if (error.response.status === 409 && error.response.data.title === "Receipt XML occurrence count mismatch") {
                    setSnackbarContent({
                        type: "error",
                        message: "O número de ocorrências no arquivo XML não corresponde ao número de ocorrências no lote. Por favor, verifique se o arquivo XML está correto e tente novamente."
                    })
                } else if (error.response.status === 409 && error.response.data.title === "Cancelled Occurrence in Receipt") {
                    setSnackbarContent({
                        type: "error",
                        message: "Existe ao menos uma ocorrência cancelada no lote. Não foi possível enviar o recibo."
                    })
                } else {
                    setSnackbarContent({
                        type: "error",
                        message: "Ocorreu um erro ao enviar o recibo. Por favor, tente novamente."
                    })
                }
            } finally {
                setIsUploading(false)
                event.target.value = ''
            }
        }

        reader.onerror = (error) => {
            console.error("File reading error:", error)
            setIsUploading(false)
            setSnackbarContent({
                type: "error",
                message: "Erro ao ler o arquivo de recibo. Por favor, tente novamente."
            })
        }
    }

    const clickableElementClasses = ["link", "subtitleSize", "bold"].join(" ")
    const readOnlyElementClasses = ["listCardItem", "normalText", "subtitleSize"].join(" ")

    return (
        <Card className="listCard">
            <input
                type="file"
                accept=".xml"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            <div
                className={clickableElementClasses}
                style={{ flex: flexColumnStyles[0], alignSelf: "center", textAlign: "center" }}
                onClick={openViewDialog}
            >
                {batch.occurrence_batch_external_id || "N/A"}
            </div>

            <div
                className={readOnlyElementClasses}
                style={{ flex: flexColumnStyles[1], alignSelf: "center", textAlign: "center" }}
            >
                {t(batch.status) || "-"}
            </div>

            <div
                className={readOnlyElementClasses}
                style={{ flex: flexColumnStyles[2], alignSelf: "center", textAlign: "center" }}
            >
                {batch.regulatory_authority === "cvm" ? t("CVM") : t("BACEN")}
            </div>

            <div
                className={readOnlyElementClasses}
                style={{ flex: flexColumnStyles[3], alignSelf: "center", textAlign: "center" }}
            >
                {moment(batch.created_at).format("DD/MM/YYYY HH:mm:ss")}
            </div>

            <div
                style={{
                    flex: flexColumnStyles[4],
                    alignSelf: "center",
                    textAlign: "center",
                    gap: "25px",
                    display: "flex",
                    justifyContent: "center"
                }}
            >

                <Tooltip title={t("Baixar XML")}>
                    <span>
                        <IconButton
                            onClick={handleDownloadClick}
                            size="small"
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                <GetAppIcon color="action" />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={t("Upload de Recibo (XML)")}>
                    <span>
                        <IconButton
                            onClick={handleUploadClick}
                            size="small"
                            disabled={isUploading || !isPendingSubmission}
                        >
                            {isUploading ? (
                                <CircularProgress size={24} style={{ color: "#4caf50" }} />
                            ) : (
                                <CloudUploadIcon style={{ color: isPendingSubmission? "#4caf50" : "disabled", opacity: isPendingSubmission ? 1 : 0.5 }} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={isDraft ? t("Editar Lote") : t("Apenas rascunhos podem ser editados")}>
                    <span>
                        <IconButton
                            onClick={openEditDialog}
                            size="small"
                            disabled={!isDraft}
                        >
                            <EditIcon color={isDraft ? "primary" : "disabled"} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={isDraft ? t("Excluir Lote") : t("Apenas rascunhos podem ser excluídos")}>
                    <span>
                        <IconButton
                            onClick={handleDeleteClick}
                            size="small"
                            disabled={isDeleting || !isDraft}
                        >
                            <DeleteIcon color={isDraft ? "error" : "disabled"} />
                        </IconButton>
                    </span>
                </Tooltip>
            </div>

            {openDeleteConfirm &&
            <Dialog
                open={openDeleteConfirm}
                onClose={handleCloseDeleteConfirm}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" style={{ color: "#0b1f82", fontWeight: "bold" }}>{t("Excluir") + " " + t("Lote") + "?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {`${t("Tem certeza que deseja excluir")}: `} 
                        <b>{`${t("lote")} `}{batch.occurrence_batch_external_id}</b>?
                        <br/>
                        {t("Esta ação não pode ser desfeita.")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteConfirm}
                        className="button onlyboarder normalText normalMediumSize"
                        disabled={isDeleting}>
                        {t("Cancelar")}
                    </Button>
                    <Button onClick={confirmDelete} 
                        className="button standard normalText normalMediumSize"
                        disabled={isDeleting}>
                    {isDeleting ? t("Excluindo") + "..." : t("Excluir")}
                    </Button>
                </DialogActions>
            </Dialog>}

            {openGenerateConfirm && (
                <Dialog
                    open={openGenerateConfirm}
                    onClose={handleCloseGenerateConfirm}
                    aria-labelledby="alert-dialog-title-generate"
                    aria-describedby="alert-dialog-description-generate"
                >
                    <DialogTitle id="alert-dialog-title-generate" style={{color: "#0b1f82", fontWeight: "bold"}}>{t("Gerar XML e fechar Lote")+"?"}</DialogTitle>
                    <DialogContent>
                    <DialogContentText id="alert-dialog-description-generate">
                        {t("Atenção: Para baixar o arquivo XML, este lote precisará ser finalizado.")}
                        <br/><br/>
                        <b>{t("Isso impedirá edições ou exclusões futuras do lote e de suas ocorrências.")}</b>
                        <br/><br/>
                        {t("Deseja Continuar")+"?"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGenerateConfirm}
                        className="button onlyboarder normalText normalMediumSize"
                        disabled={isDownloading}>
                        {t("Cancelar")}
                    </Button>
                    <Button onClick={confirmGenerateAndDownload}
                        className="button standard normalText normalMediumSize"
                        disabled={isDownloading}>
                        {isDownloading ? <CircularProgress size={20} color="inherit"/> : t("Confirmar e Baixar")}
                    </Button>
                </DialogActions>
            </Dialog>
        )}
        </Card>
    )
}

export default CoafBatchCard